/**
 * Article structure list editor: intro/content/promo blocks, block settings, promo attachments.
 * Phase 5 extraction (05-03). Phase 7: sync to adjacent blocks only (CUST-06).
 */

/** Return adjacent content block numbers for a promo at index idx (1-based). Block above = last content before promo; block below = first content after. */
function getAdjacentBlockNumbers(articleStructure, idx) {
    const contentCountBefore = articleStructure.slice(0, idx).filter((i) => i === 'content').length;
    const contentCountAfter = articleStructure.slice(idx + 1).filter((i) => i === 'content').length;
    const blockAbove = contentCountBefore >= 1 ? contentCountBefore : null;
    const blockBelow = contentCountAfter >= 1 ? contentCountBefore + 1 : null;
    const out = [];
    if (blockAbove != null) out.push(blockAbove);
    if (blockBelow != null && blockBelow !== blockAbove) out.push(blockBelow);
    return out;
}

export default function ArticleStructureEditor({
    articleStructure,
    blockSettings,
    blockSettingsOpen,
    promos,
    promoAttachments,
    contentBlockNumbers,
    enableTopicPromoBlocks,
    insertPromo,
    resetStructure,
    updateBlockSetting,
    toggleBlockSettingsPanel,
    moveBlock,
    removeIntro,
    removePromo,
    setPromoAttachment,
}) {
    let promoCounter = 0;
    return (
        <div className="structure-editor">
            <div className="structure-header">
                <div>
                    <h3>Article Structure</h3>
                    <p className="structure-hint">Insert or remove promo blocks to control layout.</p>
                </div>
                <div className="structure-actions">
                    <button className="btn-secondary small" onClick={() => insertPromo(Math.max(0, articleStructure.length - 1))}>
                        + Add Promo Block
                    </button>
                    <button className="btn-ghost small" onClick={resetStructure}>
                        Reset
                    </button>
                </div>
            </div>

            <div className="structure-list">
                {articleStructure.map((item, idx) => {
                    if (item === 'promo') promoCounter += 1;
                    const contentNumber = articleStructure.slice(0, idx + 1).filter(i => i === 'content').length;
                    const promoIndex = Math.max(0, promoCounter - 1);
                    const attachedToBlock = promoAttachments[String(promoIndex)] ?? '';
                    const contentCfg = blockSettings[String(contentNumber)] || {};
                    const label = item === 'intro'
                        ? 'Intro'
                        : item === 'promo'
                            ? `Promo ${promoCounter}`
                            : `Block ${contentNumber}`;
                    return (
                        <div key={`${item}-${idx}`} className={`structure-item type-${item}`}>
                            <div className="structure-label">
                                <span className="structure-pill">{label}</span>
                                {item === 'promo' && (
                                    <span className="structure-sub">
                                        Use promo copy #{Math.min(promoCounter, promos.length)}
                                    </span>
                                )}
                                {enableTopicPromoBlocks && item === 'promo' && (() => {
                                    const adjacentBlocks = getAdjacentBlockNumbers(articleStructure, idx);
                                    const valueInAdjacent = adjacentBlocks.length && (attachedToBlock === '' || adjacentBlocks.includes(Number(attachedToBlock)));
                                    const selectValue = valueInAdjacent ? attachedToBlock : '';
                                    return (
                                        <div className="structure-sub attachment-row">
                                            <label>Sync to block:</label>
                                            <select
                                                value={selectValue}
                                                onChange={(e) => setPromoAttachment(promoIndex, e.target.value ? Number(e.target.value) : null)}
                                            >
                                                <option value="">None (whole article)</option>
                                                {adjacentBlocks.length === 1 && (
                                                    <option value={adjacentBlocks[0]}>Block {adjacentBlocks[0]}</option>
                                                )}
                                                {adjacentBlocks.length === 2 && (
                                                    <>
                                                        <option value={adjacentBlocks[0]}>Block above (Block {adjacentBlocks[0]})</option>
                                                        <option value={adjacentBlocks[1]}>Block below (Block {adjacentBlocks[1]})</option>
                                                    </>
                                                )}
                                            </select>
                                        </div>
                                    );
                                })()}
                                {item === 'content' && (
                                    <div className="structure-sub block-settings-summary">
                                        {contentCfg.word_count ? `~${contentCfg.word_count} words` : 'Default length'}
                                        {contentCfg.focus ? ` · Focus set` : ''}
                                    </div>
                                )}
                            </div>
                            <div className="structure-buttons">
                                {item === 'content' && (
                                    <button
                                        className="btn-small"
                                        onClick={() => toggleBlockSettingsPanel(contentNumber)}
                                        title="Block settings"
                                    >
                                        {blockSettingsOpen[String(contentNumber)] ? 'Hide settings' : 'Settings'}
                                    </button>
                                )}
                                {item === 'promo' && (
                                    <>
                                        <button
                                            className="btn-small icon"
                                            onClick={() => moveBlock(idx, 'up')}
                                            disabled={idx <= (articleStructure[0] === 'intro' ? 1 : 0)}
                                            title="Move promo up"
                                        >
                                            ↑
                                        </button>
                                        <button
                                            className="btn-small icon"
                                            onClick={() => moveBlock(idx, 'down')}
                                            disabled={idx >= articleStructure.length - 1}
                                            title="Move promo down"
                                        >
                                            ↓
                                        </button>
                                    </>
                                )}
                                {item === 'intro' && (
                                    <button
                                        className="btn-small danger"
                                        onClick={removeIntro}
                                        title="Remove intro block"
                                    >
                                        Remove
                                    </button>
                                )}
                                {item === 'promo' && (
                                    <button
                                        className="btn-small danger"
                                        onClick={() => removePromo(idx)}
                                        title="Remove this promo block"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                            {item === 'content' && blockSettingsOpen[String(contentNumber)] && (
                                <div className="block-settings-panel">
                                    <div className="setting-item">
                                        <label>{`Block ${contentNumber} word count`}</label>
                                        <select
                                            className="bw-input"
                                            value={contentCfg.word_count ?? ''}
                                            onChange={(e) => updateBlockSetting(contentNumber, { word_count: e.target.value === '' ? undefined : Number(e.target.value) })}
                                        >
                                            <option value="">Default</option>
                                            {[100, 150, 200, 250, 300, 350, 400, 500, 700, 1000, 1250, 1500, 2000].map((n) => (
                                                <option key={n} value={n}>{n} words</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="setting-item">
                                        <label>{`Block ${contentNumber} focus`}</label>
                                        <textarea
                                            className="bw-input"
                                            rows={2}
                                            value={contentCfg.focus || ''}
                                            placeholder="Optional focus for this block..."
                                            onChange={(e) => updateBlockSetting(contentNumber, { focus: e.target.value })}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
